import { useState } from 'react';
import { Link } from 'wouter';
import {
    Building2,
    CheckCircle2,
    Clock,
    CreditCard,
    ShieldCheck,
    ArrowLeft,
    AlertCircle,
} from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { usePageSEO } from '../utils/seo';

export function RequestTrialPage() {
    const [form, setForm] = useState({
        companyName: '',
        contactName: '',
        email: '',
        city: '',
        province: '',
        dockDoors: '',
        message: '',
        website: '', // anti-bot honeypot
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    usePageSEO({
        title: 'Request Warehouse Trial Access — BayWhiz Dock Scheduling',
        description:
            'Request a 30-day full-access BayWhiz warehouse trial. No credit card required and no automatic billing.',
        canonicalPath: '/request-trial',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const companyName = form.companyName.trim();
        const contactName = form.contactName.trim();
        const email = form.email.trim();
        const city = form.city.trim();
        const province = form.province.trim();
        const dockDoorsRaw = form.dockDoors.trim();
        const message = form.message.trim();

        if (!companyName || !contactName || !email || !city || !province) {
            setError('Please enter your company name, contact name, work email, city, and province/state.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid work email address.');
            return;
        }
        if (dockDoorsRaw) {
            const parsedDocks = Number(dockDoorsRaw);
            if (!Number.isInteger(parsedDocks) || parsedDocks < 1 || parsedDocks > 500) {
                setError('Number of dock doors must be a whole number between 1 and 500.');
                return;
            }
        }

        setSubmitting(true);
        try {
            const res = await fetch('/api/marketing/trial-requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    companyName,
                    contactName,
                    email,
                    city,
                    province,
                    ...(dockDoorsRaw ? { dockDoors: Number(dockDoorsRaw) } : {}),
                    ...(message ? { message } : {}),
                    ...(form.website ? { website: form.website } : {}),
                }),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) {
                setError(
                    data.error ||
                        'We could not submit your trial request right now. Please try again or email support@baywhiz.com.'
                );
                return;
            }

            setSubmitted(true);
        } catch {
            setError(
                'Network error while submitting your request. Please try again or email support@baywhiz.com.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
            <MarketingNav variant="light" />

            <main className="flex-1 py-12 sm:py-16 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="mb-6 flex items-center justify-between">
                        <Link
                            href="/"
                            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            <ArrowLeft size={16} />
                            Back to BayWhiz.com
                        </Link>
                        <Link
                            href="/pricing"
                            className="text-xs font-semibold text-teal-700 hover:text-teal-800"
                        >
                            View Pricing &amp; Trial Details &rarr;
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        {/* Form Card */}
                        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
                            {submitted ? (
                                <div
                                    data-testid="trial-request-confirmation"
                                    className="py-8 text-center space-y-4"
                                >
                                    <div className="w-12 h-12 rounded-full bg-teal-50 border border-teal-200 text-teal-600 flex items-center justify-center mx-auto">
                                        <CheckCircle2 size={24} />
                                    </div>
                                    <h1 className="text-2xl font-bold text-slate-900">
                                        Trial Access Request Received
                                    </h1>
                                    <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                                        Thank you, <strong>{form.contactName}</strong>. We have received the warehouse trial request for{' '}
                                        <strong>{form.companyName}</strong>. Once reviewed, we will follow up at{' '}
                                        <span className="font-mono font-semibold text-slate-800">{form.email}</span>{' '}
                                        with your single-use warehouse registration invitation.
                                    </p>
                                    <p className="text-xs text-slate-500 max-w-md mx-auto">
                                        Questions in the meantime? Reach us at{' '}
                                        <a
                                            href={`mailto:${PRICING_CONFIG.supportEmail}`}
                                            className="text-teal-700 font-semibold hover:underline"
                                        >
                                            {PRICING_CONFIG.supportEmail}
                                        </a>
                                        .
                                    </p>
                                    <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
                                        <Link
                                            href="/"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm transition-colors"
                                        >
                                            Return to Homepage
                                        </Link>
                                        <Link
                                            href="/login?mode=warehouse"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-colors"
                                        >
                                            Warehouse Login
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-6">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-3">
                                            <Clock size={13} />
                                            {PRICING_CONFIG.trialDays}-Day Full-Access Warehouse Trial
                                        </div>
                                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5 tracking-tight">
                                            Request Warehouse Trial Access
                                        </h1>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            Warehouse registration is available by invitation. Tell us about your facility below and we will send a single-use registration link to your work email once approved. No account creation or credit card is required to request access.
                                        </p>
                                    </div>

                                    {error && (
                                        <div
                                            data-testid="trial-request-error"
                                            role="alert"
                                            className="mb-5 p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2.5"
                                        >
                                            <AlertCircle size={18} className="shrink-0 mt-0.5 text-red-600" />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <form
                                        onSubmit={handleSubmit}
                                        data-testid="trial-request-form"
                                        className="space-y-4"
                                    >
                                        {/* Anti-bot Honeypot */}
                                        <div className="hidden" aria-hidden="true">
                                            <label htmlFor="trial-req-website">Website</label>
                                            <input
                                                id="trial-req-website"
                                                name="website"
                                                type="text"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={form.website}
                                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label
                                                    htmlFor="trial-company-name"
                                                    className="block text-sm font-medium text-slate-700 mb-1"
                                                >
                                                    Company Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="trial-company-name"
                                                    data-testid="trial-company-input"
                                                    type="text"
                                                    required
                                                    maxLength={120}
                                                    value={form.companyName}
                                                    onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                                                    placeholder="Pacific Distribution Ltd."
                                                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                                />
                                            </div>

                                            <div>
                                                <label
                                                    htmlFor="trial-contact-name"
                                                    className="block text-sm font-medium text-slate-700 mb-1"
                                                >
                                                    Contact Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="trial-contact-name"
                                                    data-testid="trial-contact-input"
                                                    type="text"
                                                    required
                                                    maxLength={120}
                                                    value={form.contactName}
                                                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                                                    placeholder="Jordan Mercer"
                                                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="trial-work-email"
                                                className="block text-sm font-medium text-slate-700 mb-1"
                                            >
                                                Work Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="trial-work-email"
                                                data-testid="trial-email-input"
                                                type="email"
                                                required
                                                maxLength={254}
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                placeholder="jordan@warehouse.com"
                                                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label
                                                    htmlFor="trial-city"
                                                    className="block text-sm font-medium text-slate-700 mb-1"
                                                >
                                                    City <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="trial-city"
                                                    data-testid="trial-city-input"
                                                    type="text"
                                                    required
                                                    maxLength={80}
                                                    value={form.city}
                                                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                                                    placeholder="Surrey"
                                                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                                />
                                            </div>

                                            <div>
                                                <label
                                                    htmlFor="trial-province"
                                                    className="block text-sm font-medium text-slate-700 mb-1"
                                                >
                                                    Province / State <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="trial-province"
                                                    data-testid="trial-province-input"
                                                    type="text"
                                                    required
                                                    maxLength={80}
                                                    value={form.province}
                                                    onChange={(e) => setForm({ ...form, province: e.target.value })}
                                                    placeholder="BC"
                                                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="trial-dock-doors"
                                                className="block text-sm font-medium text-slate-700 mb-1"
                                            >
                                                Number of Dock Doors{' '}
                                                <span className="text-slate-400 font-normal">(optional)</span>
                                            </label>
                                            <input
                                                id="trial-dock-doors"
                                                data-testid="trial-docks-input"
                                                type="number"
                                                min={1}
                                                max={500}
                                                value={form.dockDoors}
                                                onChange={(e) => setForm({ ...form, dockDoors: e.target.value })}
                                                placeholder="e.g. 6"
                                                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600"
                                            />
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="trial-message"
                                                className="block text-sm font-medium text-slate-700 mb-1"
                                            >
                                                Message{' '}
                                                <span className="text-slate-400 font-normal">(optional)</span>
                                            </label>
                                            <textarea
                                                id="trial-message"
                                                data-testid="trial-message-input"
                                                rows={3}
                                                maxLength={1000}
                                                value={form.message}
                                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                                placeholder="Tell us briefly about your receiving/shipping workflow or questions…"
                                                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-600 resize-y"
                                            />
                                        </div>

                                        <div
                                            data-testid="trial-request-privacy-notice"
                                            className="rounded-lg bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600 leading-relaxed"
                                        >
                                            BayWhiz will use the contact information you submit here solely to review and respond to your trial access request, in accordance with our{' '}
                                            <Link
                                                href="/privacy"
                                                className="font-semibold text-teal-700 hover:underline"
                                            >
                                                Privacy Policy
                                            </Link>
                                            . You will not be subscribed to marketing lists.
                                        </div>

                                        <button
                                            type="submit"
                                            data-testid="trial-request-submit-btn"
                                            disabled={submitting}
                                            className="h-12 w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-60 cursor-pointer shadow-sm"
                                        >
                                            {submitting ? 'Submitting Request...' : 'Request Trial Access'}
                                        </button>

                                        <div className="text-center text-sm text-slate-600 mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                                            <p>
                                                Already have an approved account?{' '}
                                                <Link href="/login?mode=warehouse" className="text-teal-700 font-semibold hover:underline">
                                                    Warehouse Login
                                                </Link>
                                            </p>
                                            <p>
                                                Are you a freight carrier?{' '}
                                                <Link href="/carrier-signup" className="text-teal-700 font-semibold hover:underline">
                                                    Free carrier sign up &rarr;
                                                </Link>
                                            </p>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>

                        {/* Right Column: Trial Summary */}
                        <aside
                            aria-label="30-day free trial details"
                            className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 shadow-sm space-y-6"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center mb-3">
                                    <Building2 size={18} />
                                </div>
                                <h2 className="text-lg font-bold text-slate-900 mb-1.5">
                                    What’s included in your 30-day trial
                                </h2>
                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                    Evaluate the full BayWhiz Operations plan with your receiving team before making any billing commitment.
                                </p>
                            </div>

                            <ol className="space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                        1
                                    </span>
                                    <div>
                                        <p className="font-semibold text-slate-900">Request approval</p>
                                        <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                            Submit your facility details. Once approved, you receive a single-use invitation link bound to your work email.
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                        2
                                    </span>
                                    <div>
                                        <p className="font-semibold text-slate-900">Days 1&ndash;30: Full Operations access</p>
                                        <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                            Configure dock doors, share your public carrier booking link, import POs, and track arrivals and dwell time.
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                        3
                                    </span>
                                    <div>
                                        <p className="font-semibold text-slate-900">After Day 30: Subscribe only if you continue</p>
                                        <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                            No credit card is collected and nothing is charged automatically. Contact {PRICING_CONFIG.supportEmail} if you choose to activate Starter (${PRICING_CONFIG.plans.starter.priceMonthlyUsd} USD/mo) or Operations (${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/mo) per facility.
                                        </p>
                                    </div>
                                </li>
                            </ol>

                            <div className="pt-4 border-t border-slate-200 space-y-2.5 text-xs text-slate-600">
                                <div className="flex items-center gap-2">
                                    <CreditCard size={15} className="text-teal-600 shrink-0" />
                                    <span>No credit card or automatic billing</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={15} className="text-teal-600 shrink-0" />
                                    <span>Carriers register and book appointments for free</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ShieldCheck size={15} className="text-teal-600 shrink-0" />
                                    <span>Single-use, email-bound warehouse invitations</span>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </main>

            <MarketingFooter variant="light" />
        </div>
    );
}

export default RequestTrialPage;
